import React, { useEffect, useRef, useState } from 'react';
import { useOrion } from '../context/OrionContext';
import {
  formatarDescricaoInteracao, todasLinhasSaoAlteracoes,
  separarUsuarioLogado, normalizarNome,
} from '../services/texto';
import Icon from './Icon';

// ─────────────────────────────────────────────────────────────────────────────
// A conversa de um ticket.
//
// Era uma lista de bolhas de chat — as do próprio operador de um lado, as dos
// outros do outro. O formato imitava o WhatsApp, e num ticket de suporte isso
// escondia a informação que mais importa: QUEM registrou e O QUE foi aquele
// registro (abertura? mudança automática? mensagem escrita?).
//
// Agora cada interação é uma LINHA de duas colunas:
//
//   · à esquerda, o autor — avatar com as iniciais, o nome, o papel
//     (Solicitante/Operador) e a data. É a coluna que se lê na vertical
//     quando se quer saber por quantas mãos o ticket passou;
//   · à direita, o registro — um cartão com o TIPO do evento no topo
//     ("Abertura do ticket", "Alteração no ticket", "Atualização"…) e o
//     conteúdo embaixo.
//
// A COR é do autor, não do tipo: solicitante, o próprio operador logado, a
// Análise Suporte (o automático do MySuite) e os demais colegas têm cada um a
// sua. Assim dá para varrer a lista e ver de longe onde o cliente falou e onde
// o robô mexeu, sem ler nome nenhum.
//
// Continua valendo o resto: mais antigas no topo, a visualização começa no fim,
// interação privada é marcada, e anexo do Space Area vira imagem clicável.
// ─────────────────────────────────────────────────────────────────────────────

/** Iniciais para o avatar: primeiro e último nome. */
function iniciaisDe(nome) {
  const partes = String(nome || '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

/**
 * O "tom" da linha — quem escreveu, e por consequência a cor do avatar e do
 * cartão. Quatro valores, cada um com um significado que o operador reconhece
 * de olho: `solicitante` (o cliente), `eu` (ele mesmo), `auto` (a Análise
 * Suporte, que é automação) e `operador` (qualquer colega).
 */
function tomDoAutor(autor, temOperador, nomeOperadorLogado) {
  if (!temOperador) return 'solicitante';
  const n = normalizarNome(autor);
  if (n.startsWith('analise suporte')) return 'auto';
  if (nomeOperadorLogado && n === normalizarNome(nomeOperadorLogado)) return 'eu';
  return 'operador';
}

/**
 * O título do cartão: o TIPO do registro.
 *
 * A ordem das perguntas é a da leitura — primeiro se é a abertura, depois se é
 * mudança automática de campo e só então se é conversa:
 *
 *   Abertura do ticket  → a primeira interação, onde o cliente descreve o caso;
 *   Análise Suporte     → mudança feita pelo automático do MySuite (previsão,
 *                         triagem). Leva o nome dele porque é assim que a
 *                         equipe se refere a esse registro;
 *   Atualização         → mudança de OPERADOR ou de STATUS: o ticket trocou de
 *                         mão ou de fila, que é o que se procura no histórico;
 *   Alteração no ticket → as demais mudanças de campo (classificação,
 *                         ocorrência, criticidade);
 *   Interação           → alguém escreveu de fato.
 */
function tituloDoRegistro({ primeira, ehAviso, tom, corpo, privado }) {
  if (primeira) return 'Abertura do ticket';
  if (ehAviso) {
    if (tom === 'auto') return 'Análise Suporte';
    return /\b(operador|status)\b/i.test(corpo) ? 'Atualização' : 'Alteração no ticket';
  }
  return privado ? 'Interação privada' : 'Interação';
}

/**
 * A data que o cabeçalho mostra.
 *
 * A primeira interação pode ser SINTÉTICA: quando `/tickets/{n}` traz o texto
 * de abertura e o endpoint de interações não, `buscarInteracoes` a insere no
 * topo com a string "Abertura do ticket" no lugar da data. Mostrá-la seria
 * repetir, embaixo do nome, o mesmo rótulo que já está no título do cartão.
 */
function dataHoraDe(it) {
  const data = String(it.dataInteracao || '').trim();
  if (!data || !/\d/.test(data)) return '';
  return `${data} ${String(it.horaInteracao || '').substring(0, 5)}`.trim();
}

/** Uma linha da conversa. */
function ItemInteracao({ it, primeira, solicitanteFallback, nomeOperadorLogado, aoAmpliar }) {
  // Sem operador (itc_opecodigo = 0) → interação do próprio solicitante.
  const temOperador = !!it.operador;
  const descricao = it.descricao || '';
  const privado = (it.privado === 1 || it.privado === true);

  // O MySuite às vezes carimba "[Usuário Logado: Fulano]" na frente do texto.
  // Quando existe, ele é o autor de verdade — o campo `operador` do registro
  // pode ser a fila, e não a pessoa.
  const { autor, corpo } = separarUsuarioLogado(descricao);
  const nome = autor || it.operador || solicitanteFallback || '—';
  const texto = (autor ? corpo : descricao).trim();

  // Alteração automática de campo: todas as linhas são "Campo -> valor".
  const ehAviso = todasLinhasSaoAlteracoes(texto);

  const tom = tomDoAutor(nome, temOperador, nomeOperadorLogado);
  const titulo = tituloDoRegistro({ primeira, ehAviso, tom, corpo: texto, privado });
  const dataHora = dataHoraDe(it);

  return (
    <div className={`itc-linha itc-${tom}${privado ? ' itc-privada' : ''}`}>
      <div className="itc-quem">
        <div className="itc-avatar">{iniciaisDe(nome)}</div>
        <div className="itc-quem-txt">
          <div className="itc-nome" title={nome}>{nome}</div>
          <div className="itc-papel">{temOperador ? 'Operador' : 'Solicitante'}</div>
          {dataHora && <div className="itc-data">{dataHora}</div>}
        </div>
      </div>

      <div className="itc-card">
        <div className="itc-titulo">
          {titulo}
          {privado && <span className="itc-selo-privado">PRIVADO</span>}
        </div>
        <div
          className="itc-corpo"
          onClick={aoAmpliar}
          dangerouslySetInnerHTML={{ __html: formatarDescricaoInteracao(texto) }}
        />
      </div>
    </div>
  );
}

/**
 * A conversa inteira, para ser embutida onde quem chama quiser.
 *
 * `rolarParaOFim` desliga a rolagem automática para quem monta a lista no meio
 * de uma tela — puxar a página inteira para baixo sozinha assustaria.
 */
export default function ListaInteracoes({
  interacoes = [],
  carregando = false,
  solicitante = '',
  rolarParaOFim = true,
}) {
  const { operador } = useOrion();
  const corpoRef = useRef(null);
  const [ampliada, setAmpliada] = useState(null);

  // Começa nas mensagens mais recentes, como num chat.
  useEffect(() => {
    if (rolarParaOFim && corpoRef.current) {
      corpoRef.current.scrollTop = corpoRef.current.scrollHeight;
    }
  }, [interacoes, rolarParaOFim]);

  // Esc fecha a imagem ampliada. Quem hospeda a lista trata o Esc dele.
  useEffect(() => {
    if (!ampliada) return undefined;
    const aoTeclar = (e) => { if (e.key === 'Escape') setAmpliada(null); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [ampliada]);

  // Clique num anexo amplia. O <img> vem de dentro do HTML montado, então não
  // dá para pendurar onClick nele — a captura acontece no contêiner.
  const talvezAmpliar = (e) => {
    const alvo = e.target;
    if (alvo && alvo.tagName === 'IMG' && alvo.dataset.full) setAmpliada(alvo.dataset.full);
  };

  return (
    <>
      <div className="interacoes-lista" ref={corpoRef}>
        {carregando && (
          <div className="interacoes-panel-empty">
            <Icon name="rotate-cw" size={16} className="orion-spin" /> Carregando interações…
          </div>
        )}

        {!carregando && !interacoes.length && (
          <div className="interacoes-panel-empty">Nenhuma interação encontrada.</div>
        )}

        {!carregando && interacoes.map((it, i) => (
          <ItemInteracao
            key={i}
            it={it}
            primeira={i === 0}
            solicitanteFallback={solicitante}
            nomeOperadorLogado={operador?.nome}
            aoAmpliar={talvezAmpliar}
          />
        ))}
      </div>

      {/* A imagem ampliada fica por cima de tudo — inclusive do modal que
          hospeda a lista, que é por onde ela quase sempre é aberta. */}
      {ampliada && (
        <div className="lightbox" onClick={() => setAmpliada(null)}>
          <img src={ampliada} alt="Anexo do ticket ampliado" />
        </div>
      )}
    </>
  );
}
